import useFetchedDataWithPagination from 'hooks/useFetchedDataWithPagination'
import { displayPrice, toStringNumber } from 'utils/string'
import { useHistory, useParams } from 'react-router-dom'
import { toDateTime, toShortDate } from 'utils/date'
import { useEffect, useRef, useState } from 'react'
import ArrowBtn from 'components/buttons/ArrowBtn'
import useTranslation from 'hooks/useTranslation'
import useFetchedData from 'hooks/useFetchedData'
import Input from 'components/inputs/Input'
import Filters from 'components/Filters'
import Loading from 'components/Loading'
import Error from 'components/Error'
import * as fields from './fields'
import * as urls from 'rest/urls'
import Item from '../Item'

const sortOptions = [{
  name: 'default',
  value: ''
}, {
  name: 'mostExpensiveFirst',
  value: 'grossAmount-desc'
}, {
  name: 'cheapestFirst',
  value: 'grossAmount-asc'
}, {
  name: 'newestFirst',
  value: 'documentDate-desc'
}, {
  name: 'oldestFirst',
  value: 'documentDate-asc'
}]

const InvoicesList = ({ amount, invoicesToPay, setInvoicesToPay, customWrapperClassName = '', showPayments }) => {

  const { id } = useParams()
  const initialFilters = id ? { warehousesIds: [id] } : {}
  const [filters, setFilters] = useState(initialFilters)
  const [selectedSort, selectSort] = useState(sortOptions[0].value)
  const [isOnlyUnpaidInvoices, showOnlyUnpaidInvoices] = useState(Object.keys(invoicesToPay).length > 0)

  const listRef = useRef(null)
  const { t, lang } = useTranslation('account')
  const unpaidInvoices = useFetchedData(getListUrl(initialFilters, selectedSort, '0'), 'data', 'GET')
  const paidInvoices = useFetchedDataWithPagination(getListUrl(filters, selectedSort, '1'), listRef)

  const { replace } = useHistory()
  const downloadFv = (link, updateItem = false) => {
    if (link) window.open(link, '_blank')
    if (updateItem) {
      paidInvoices.reload()
    }
  }

  const getTitle = (documentType, isPaid) => {
    switch (documentType) {
      case 'receipt': return isPaid ? 'receipt' : 'receiptUnpaid'
      case 'bill': return 'bill'
      default: return 'invoiceShort'
    }
  }

  const renderInvoice = ({ id, documentDate, documentType, fullNumber, link, lastAccessDate, isPaid, grossAmount, remainingAmount }, i) => {
    return <Item
      key={i}
      label={toShortDate(documentDate, lang)}
      title={`${t(getTitle(documentType, isPaid))} ${fullNumber}`}
      icon={<div
        className={`circle center --${lastAccessDate ? 'checked' : 'unchecked'}`}
        onClick={() => {
          downloadFv(link, !lastAccessDate)
        }} >
        <i className='icon icon--download' />
      </div>}
      desc={<>
        <div>
          {isPaid
            ? `${t('paidAmountShort')}: ${grossAmount}`
            : `${t('amountToPay')}: ${remainingAmount}${remainingAmount !== grossAmount ? ` ${t('of')} ${grossAmount}` : ''}`}
        </div>
        <div>{lastAccessDate ? `${t('downloaded')} ${toDateTime(lastAccessDate, lang)}` : t('notDownloaded')}</div>
      </>}
      right={invoicesToPay[id] !== undefined
        ? <div className='input__container'>
          <Input
            value={invoicesToPay[id]}
            changeHandler={e => {
              const { value } = e.target
              if (value && !value.match(/^\d+\.?\d{0,2}$/)) return
              setInvoicesToPay(prev => {
                if (!value) {
                  return { ...prev, [id]: (value || '') }

                } else {
                  const potentialNewValue = Number(value)
                  const maxInvoiceAmount = Number(remainingAmount)
                  const newValue = potentialNewValue > maxInvoiceAmount ? toStringNumber(maxInvoiceAmount) : value
                  return { ...prev, [id]: newValue }
                }
              })
            }}
          />
          <i
            className='icon icon--close'
            onClick={() => {
              setInvoicesToPay(prev => {
                const { [id]: _, ...rest } = prev
                if (Object.keys(rest).length === 0) {
                  showOnlyUnpaidInvoices(false)
                }
                return rest
              })
            }} />
        </div>
        : !isPaid
          ? <div>
            <ArrowBtn
              text={t('payInvoice')}
              showArrow={false}
              action={() => {
                showOnlyUnpaidInvoices(true)
                setInvoicesToPay(prev => ({ ...prev, [id]: remainingAmount }))
              }}
            />
          </div>
          : null}
    />
  }

  useEffect(() => {
    if (unpaidInvoices.data && unpaidInvoices.data.length > 0 && window.location.search) {
      const query = new URLSearchParams(window.location.search)
      const selectAll = query.get('selectAll')
      if (selectAll) {
        setInvoicesToPay(
          unpaidInvoices.data.reduce((r, item) => {
            return { ...r, [item.id]: item.remainingAmount }
          }, {})
        )
        showOnlyUnpaidInvoices(true)
        replace(window.location.pathname)
      }
    }
  }, [replace, setInvoicesToPay, unpaidInvoices.data])

  /**
   * eh...
   */
  useEffect(() => {
    const popup = document.querySelector(`.${customWrapperClassName || 'account__list'}`)
    if (popup) {
      listRef.current = popup
      popup.addEventListener('scroll', paidInvoices.handleScroll)
      return () => {
        popup.removeEventListener('scroll', paidInvoices.handleScroll)
      }
    }
  }, [customWrapperClassName, paidInvoices.handleScroll])


  return (
    <>
      <Filters
        showFiltersBtn
        filters={filters}
        setFilters={setFilters}
        fields={fields[lang]}
        sortOptions={sortOptions}
        selectedSort={selectedSort}
        selectSort={selectSort}
      />

      <div className='account__list account__invoices'>
        {unpaidInvoices.data && unpaidInvoices.data?.length > 0 && <>
          {!isOnlyUnpaidInvoices && <p>{t('amountDue')}</p>}
          {unpaidInvoices.data.map(renderInvoice)}
          {!isOnlyUnpaidInvoices && <p>{t('remaining')}</p>}
        </>}
        {paidInvoices.data
          ? paidInvoices.data?.length > 0
            ? isOnlyUnpaidInvoices
              ? null
              : paidInvoices.data.map(renderInvoice)
            : unpaidInvoices.data?.length === 0
              ? <Error
                staticPos
                text={t(Object.keys(filters).length > (id ? 1 : 0) ? 'noItems' : 'noInvoices')} />
              : null
          : (paidInvoices.isLoading || unpaidInvoices.isLoading)
            ? <Loading middleOfPage={!customWrapperClassName} />
            : <Error staticPos />}

        {isOnlyUnpaidInvoices && <ArrowBtn
          enlarged
          key={amount}
          disabled={!amount}
          className='account__flyingButton'
          text={`${t('pay')} ${displayPrice(amount)}`}
          action={showPayments}
        />}
      </div>
    </>
  )
}

const getListUrl = (filters, selectedSort, isPaid) => {
  const params = generateFilterParams(filters, selectedSort, isPaid)
  return `${urls.invoices}${params ? `?${params}` : ''}`
}

const generateFilterParams = (filters, selectedSort, isPaid) => {
  let filtersParam = ''
  if (isPaid) {
    filtersParam += `isPaid::${isPaid}`
  }

  for (const key in filters) {
    if (key !== 'warehousesIds') {
      if (filtersParam) filtersParam += ';'
      if (key === 'date') {
        filtersParam += `documentDate::${filters[key]?.start || ''},${filters[key]?.end || ''}`
      } else {
        filtersParam += `${key}::${filters[key]}`
      }
    }
  }

  const params = new URLSearchParams()
  if (filtersParam) params.append('filters', filtersParam)
  if (filters['warehousesIds'] && Array.isArray(filters['warehousesIds']) && filters['warehousesIds'].length > 0) {
    params.append('warehousesIds', filters['warehousesIds'].reduce((r, id) => r += (r ? ',' : '') + id, ''))
  }
  if (selectedSort) {
    const [orderBy, sortedBy] = selectedSort.split('-')
    if (orderBy && sortedBy) {
      params.append('orderBy', orderBy)
      params.append('sortedBy', sortedBy)
    }
  }
  if (isPaid === '0') {
    params.append('limit', 50)
  }
  return params.toString()
}

export default InvoicesList